/**
 * ClinicTabPage — Treatment Day Dashboard (Dr. Luis Phase A, Jun 12 2026).
 *
 * A physician-only clinical surface that assembles the patient's treatment-day
 * picture from existing platform data + the doctor-authored Medical Summary.
 * Priority band on top — Diagnosis → Risk Flags → Therapy → Allergies →
 * Medications — so a physician can orient in seconds, with full sections below
 * and a one-page printable Clinical Treatment Order Sheet.
 *
 * Backend: clinic:treatment-day:get (assemble) + clinic:summary:save.
 * Self-gates on physician:read. (Phase C introduces a clinical-only role and
 * removes CRM access for doctors; Phase B adds structured Risk Flags +
 * Treatment Checklist + Assigned Team fields.)
 */
import React, { useState, useEffect, useCallback } from "react";
import ContactPicker from "../components/ContactPicker";
import {
  Stethoscope, AlertTriangle, Syringe, Pill, ShieldAlert, Loader2,
  Printer, Save, FileCheck2, CalendarClock, User as UserIcon, Brain,
} from "lucide-react";

const CLEARANCE_LABELS = {
  approved: "Approved",
  approved_with_precautions: "Approved with Precautions",
  pending_records: "Pending Additional Records",
  not_approved: "Not Approved",
};
const CLEARANCE_COLOR = {
  approved: "#2d6b2d",
  approved_with_precautions: "#7c5210",
  pending_records: "#1f4f96",
  not_approved: "#b00020",
};
const MODALITY_LABEL = {
  msc: "MSC", muse: "MUSE", exosome: "Exosomes", prp: "PRP", nk: "NK", peptide: "Peptide", other: "Other",
};
// Mirror of routes/_clinic_summary.js catalogs (UI labels).
const RISK_FLAGS = [
  ["anticoagulants", "Anticoagulants"], ["diabetes", "Diabetes"], ["hypertension", "Hypertension"],
  ["cardiac_disease", "Cardiac Disease"], ["prior_stroke", "Prior Stroke"], ["seizure_history", "Seizure History"],
  ["active_infection", "Active Infection"], ["cancer_history", "Cancer History"], ["immunosuppression", "Immunosuppression"],
  ["allergies", "Allergies"], ["sedation_risk", "Sedation Risk"], ["difficult_iv", "Difficult IV Access"],
  ["mobility_assistance", "Mobility Assistance Needed"],
];
const CHECKLIST = {
  Before: [["identity_verified", "Patient Identity Verified"], ["consent_verified", "Consent Verified"], ["labs_reviewed", "Labs Reviewed"], ["clearance_reviewed", "Medical Clearance Reviewed"], ["plan_confirmed", "Therapy Plan Confirmed"]],
  During: [["product_verified", "Product Verified"], ["route_confirmed", "Route Confirmed"], ["procedure_completed", "Procedure Completed"]],
  After: [["patient_stable", "Patient Stable"], ["discharge_given", "Discharge Instructions Given"], ["followup_scheduled", "Follow-Up Scheduled"]],
};
const TEAM_ROLES = [
  ["treating_physician", "Treating Physician"], ["consulting_physician", "Consulting Physician"],
  ["nurse", "Nurse"], ["driver", "Driver"], ["pem", "PEM"], ["senior_pem", "Senior PEM"],
];

function hasPerm(user, p) { return new Set((user && user.permissions) || []).has(p); }
function esc(s) {
  return String(s == null ? "" : s).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
function fmtDate(d) { if (!d) return "—"; const x = new Date(d); return Number.isNaN(x.getTime()) ? "—" : x.toLocaleDateString(); }
function age(dob) {
  if (!dob) return null; const d = new Date(dob); if (Number.isNaN(d.getTime())) return null;
  const n = new Date(); let a = n.getFullYear() - d.getFullYear();
  const m = n.getMonth() - d.getMonth(); if (m < 0 || (m === 0 && n.getDate() < d.getDate())) a--;
  return a;
}

export default function ClinicTabPage({ user }) {
  const canRead = hasPerm(user, "physician:read");
  const canWrite = hasPerm(user, "physician:write");

  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [tstate, setTstate] = useState(null); // { riskFlags:Set, checklist:{}, team:{} }
  const [autoFlags, setAutoFlags] = useState([]);
  const [tbusy, setTbusy] = useState(false);
  // Phase D — Clinical AI (feature-flagged; capability tells us if enabled).
  const [capability, setCapability] = useState(null);
  const [conditions, setConditions] = useState("");
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  // Load the contact list once for the picker.
  useEffect(() => {
    if (!canRead) return;
    // Clinical-only patient list (no CRM access required) — see clinic:patients:list.
    window.zignagenixAPI.invoke("clinic:patients:list", { limit: 2000 })
      .then((r) => setContacts(Array.isArray(r) ? r : (r && r.contacts) || []))
      .catch(() => setContacts([]));
    window.zignagenixAPI.invoke("clinical-research:capability")
      .then((c) => setCapability(c || null)).catch(() => setCapability(null));
  }, [canRead]);

  const load = useCallback(async (cid) => {
    if (!cid) return;
    setLoading(true); setError(null);
    try {
      const r = await window.zignagenixAPI.invoke("clinic:treatment-day:get", { contactId: cid });
      if (r && r.error) { setError(r.message || r.code); setData(null); }
      else {
        setData(r);
        const s = r.summary || {};
        setForm({
          primary_diagnosis: s.primary_diagnosis || "", secondary_diagnoses: s.secondary_diagnoses || "",
          key_symptoms: s.key_symptoms || "", functional_limitations: s.functional_limitations || "",
          patient_goals: s.patient_goals || "", clearance_status: s.clearance_status || "",
          clearance_notes: s.clearance_notes || "",
        });
        const cl = {};
        Object.entries(r.treatment_checklist || {}).forEach(([k, v]) => { cl[k] = !!(v && v.checked); });
        setTstate({
          riskFlags: new Set(Array.isArray(r.risk_flags) ? r.risk_flags : []),
          checklist: cl,
          team: { ...(r.assigned_team || {}) },
        });
        setAutoFlags(Array.isArray(r.auto_suggested_risk_flags) ? r.auto_suggested_risk_flags : []);
      }
    } catch (e) { setError(e.message); setData(null); }
    setLoading(false);
  }, []);

  async function saveSummary() {
    if (!contactId || !form) return;
    setBusy(true); setError(null);
    try {
      const r = await window.zignagenixAPI.invoke("clinic:summary:save", { contactId, ...form });
      if (r && r.error) setError(r.message || r.code);
      else await load(contactId);
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function saveTreatmentState() {
    if (!contactId || !tstate) return;
    setTbusy(true); setError(null);
    try {
      const r = await window.zignagenixAPI.invoke("clinic:treatment-state:save", {
        contactId,
        riskFlags: Array.from(tstate.riskFlags),
        checklist: tstate.checklist,
        assignedTeam: tstate.team,
      });
      if (r && r.error) setError(r.message || r.code);
      else await load(contactId);
    } catch (e) { setError(e.message); }
    setTbusy(false);
  }

  function toggleFlag(key) {
    setTstate((t) => {
      const s = new Set(t.riskFlags);
      if (s.has(key)) s.delete(key); else s.add(key);
      return { ...t, riskFlags: s };
    });
  }

  async function runAI() {
    if (!contactId || conditions.trim().length < 3) return;
    setAiBusy(true); setError(null); setAi(null);
    try {
      const r = await window.zignagenixAPI.invoke("clinical-research:suggest", { contactId, conditions });
      if (r && r.error) setError(r.message || r.code);
      else setAi(r);
    } catch (e) { setError(e.message); }
    setAiBusy(false);
  }

  function printOrderSheet() {
    if (!data || !data.order_sheet) return;
    const o = data.order_sheet;
    const row = (label, val) => `<tr><th>${esc(label)}</th><td>${esc(val == null || val === "" ? "—" : val)}</td></tr>`;
    const therapy = (o.orderedTherapy || []).map((t) =>
      `<li><b>${esc(MODALITY_LABEL[t.modality] || t.modality)}</b>${t.count != null ? ` — ${esc(t.count)} ${esc(t.unit || "")}` : ""}${t.route ? ` · ${esc(t.route.replace(/_/g, " "))}` : ""}${t.lot ? ` · lot ${esc(t.lot)}` : ""}${t.viability != null ? ` · ${esc(t.viability)}% viable` : ""} ${t.hasQc ? "✓ QC on file" : "⚠ QC missing"}</li>`
    ).join("") || "<li>—</li>";
    const allergies = (o.allergies || []).map((a) => `<li>${esc(a.allergen)}${a.severity ? ` (${esc(a.severity)})` : ""}${a.reaction ? ` — ${esc(a.reaction)}` : ""}</li>`).join("") || "<li>None recorded</li>";
    const meds = (o.medications || []).map((m) => `<li>${esc(m.drug)}${m.dose ? ` ${esc(m.dose)}` : ""}${m.frequency ? ` ${esc(m.frequency)}` : ""}${m.reason ? ` — ${esc(m.reason)}` : ""}</li>`).join("") || "<li>None recorded</li>";

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Clinical Treatment Order Sheet</title>
<style>
  @page { size: letter; margin: 0.6in; }
  body { font-family: -apple-system, Arial, sans-serif; color: #111; font-size: 12px; }
  h1 { font-size: 18px; margin: 0 0 2px; } .sub { color:#555; margin:0 0 12px; font-size:11px; }
  table { width:100%; border-collapse:collapse; margin-bottom:10px; }
  th { text-align:left; width:170px; vertical-align:top; padding:3px 8px 3px 0; color:#333; }
  td { padding:3px 0; }
  h2 { font-size:13px; border-bottom:1px solid #999; padding-bottom:2px; margin:12px 0 6px; }
  ul { margin:4px 0; padding-left:18px; } li { margin:2px 0; }
  .clear { font-weight:700; } .sig { margin-top:28px; border-top:1px solid #333; width:280px; padding-top:4px; font-size:11px; }
  .banner { background:#fdecec; border:1px solid #e0a0a0; padding:6px 8px; border-radius:4px; margin:6px 0; }
</style></head><body>
  <h1>Clinical Treatment Order Sheet</h1>
  <p class="sub">ZignaGenix — generated ${esc(new Date().toLocaleString())} · for clinician review</p>
  <table>
    ${row("Patient", o.patientName)}
    ${row("MRN / ID", o.mrn || o.patientId)}
    ${row("Age / Gender", `${o.age != null ? o.age : "—"} / ${o.gender || "—"}`)}
    ${row("Clinic", o.clinic)}
    ${row("Primary Diagnosis", o.diagnosis)}
    ${row("Secondary", o.secondaryDiagnoses)}
    ${row("Goals", o.goals)}
    ${row("Clearance", CLEARANCE_LABELS[o.clearanceStatus] || o.clearanceStatus)}
  </table>
  <h2>Ordered Therapy · Cell Counts · Lot Numbers</h2>
  <ul>${therapy}</ul>
  <h2>Allergies</h2>
  <ul>${allergies}</ul>
  <h2>Current Medications</h2>
  <ul>${meds}</ul>
  <div class="sig">Physician signature / date</div>
  <script>window.onload=function(){window.print();};</script>
</body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  if (!canRead) return <div style={{ padding: 24, color: "#5a6478" }}>You don't have access to the Clinic dashboard.</div>;

  const p = (data && data.patient) || {};
  const s = (data && data.summary) || {};

  return (
    <div style={{ padding: "16px 24px", maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <Stethoscope size={22} color="#2E8B98" />
        <div>
          <h1 style={{ fontSize: 21, margin: 0 }}>Clinic — Treatment Day</h1>
          <p style={{ color: "#5a6478", fontSize: 13, margin: 0 }}>Physician clinical dashboard. Diagnosis · Risk · Therapy · Allergies · Meds.</p>
        </div>
      </div>

      <div style={{ marginBottom: 16, maxWidth: 460 }}>
        <label style={lbl}>Patient</label>
        <ContactPicker contacts={contacts} name="clinic_contact" inputStyle={inp}
          onSelect={(c) => { setContactId(c.id); load(c.id); }} required={false} />
      </div>

      {error && <div style={errBox}><AlertTriangle size={14} /> {error}</div>}
      {loading && <div style={{ color: "#9aa4b2", padding: 12 }}><Loader2 size={14} className="spin" /> Loading…</div>}

      {data && !loading && (
        <>
          {/* ── Priority band ───────────────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
            <Band title="Diagnosis" icon={<FileCheck2 size={13} />}>{s.primary_diagnosis || "—"}</Band>
            <Band title="Risk Flags" icon={<ShieldAlert size={13} />} danger>
              {(data.risk_flags && data.risk_flags.length) ? data.risk_flags.join(", ") : <span style={{ color: "#9aa4b2" }}>none flagged (Phase B)</span>}
            </Band>
            <Band title="Therapy" icon={<Syringe size={13} />}>
              {(data.administrations || []).length ? `${data.administrations.length} item(s)` : "—"}
            </Band>
            <Band title="Allergies" icon={<AlertTriangle size={13} />} danger={(data.allergies || []).length > 0}>
              {(data.allergies || []).length ? data.allergies.map((a) => a.allergen).join(", ") : "None"}
            </Band>
            <Band title="Medications" icon={<Pill size={13} />}>
              {(data.medications || []).length || "0"}
            </Band>
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <button onClick={printOrderSheet} style={btnPrimary}><Printer size={13} /> Print Treatment Order Sheet</button>
          </div>

          {/* ── Risk Flags banner (§5) ──────────────────────────────── */}
          {tstate && (
            <div style={{ border: "2px solid #e0a0a0", background: "#fdecec", borderRadius: 6, padding: 14, marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
                <ShieldAlert size={16} color="#b00020" />
                <strong style={{ fontSize: 14, color: "#b00020" }}>Risk Flags</strong>
                <span style={{ fontSize: 11, color: "#9a6a6a" }}>tap to toggle · auto-suggested from chart are marked ●</span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {RISK_FLAGS.map(([key, label]) => {
                  const on = tstate.riskFlags.has(key);
                  const suggested = autoFlags.includes(key);
                  return (
                    <button key={key} onClick={() => canWrite && toggleFlag(key)} disabled={!canWrite}
                      style={{
                        padding: "5px 11px", fontSize: 12, borderRadius: 14, cursor: canWrite ? "pointer" : "default",
                        fontWeight: on ? 700 : 500,
                        background: on ? "#b00020" : "#fff", color: on ? "#fff" : "#7a2222",
                        border: `1px solid ${on ? "#b00020" : "#e0a0a0"}`,
                      }}>
                      {suggested && !on ? "● " : ""}{label}
                    </button>
                  );
                })}
              </div>
              {canWrite && (
                <button onClick={saveTreatmentState} disabled={tbusy} style={{ ...btnPrimary, marginTop: 10, background: "#b00020" }}>
                  {tbusy ? <><Loader2 size={12} className="spin" /> Saving…</> : <><Save size={13} /> Save Risk Flags / Checklist / Team</>}
                </button>
              )}
            </div>
          )}

          {/* ── Identification + Appointment ────────────────────────── */}
          <Section title="Patient Identification" icon={<UserIcon size={15} />}>
            <Grid items={[
              ["Name", [p.first_name, p.last_name].filter(Boolean).join(" ") || data.contact_name],
              ["MRN / ID", p.mrn || data.patient_id || "—"],
              ["DOB / Age", `${fmtDate(p.date_of_birth)}${age(p.date_of_birth) != null ? ` (${age(p.date_of_birth)})` : ""}`],
              ["Gender", p.gender || "—"],
              ["Mobile", p.phone || (data && data.contact_name ? "—" : "—")],
              ["Location", [p.city, p.state].filter(Boolean).join(", ") || "—"],
            ]} />
          </Section>

          <Section title="Therapy Appointment" icon={<CalendarClock size={15} />}>
            <Grid items={[
              ["Clinic", data.clinic_name || (data.appointment && data.appointment.clinic_name) || "—"],
              ["Appointment", data.appointment ? `${data.appointment.title || data.appointment.appointment_type || ""} · ${fmtDate(data.appointment.start_time)}` : "—"],
              ["Procedure scheduled", data.proposal && data.proposal.procedure_scheduled_at ? fmtDate(data.proposal.procedure_scheduled_at) : "—"],
            ]} />
          </Section>

          {/* ── Assigned Team (§2) ──────────────────────────────────── */}
          {tstate && (
            <Section title="Assigned Team" icon={<UserIcon size={15} />}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                {TEAM_ROLES.map(([key, label]) => (
                  <div key={key}>
                    <label style={lbl}>{label}</label>
                    <input type="text" value={tstate.team[key] || ""} disabled={!canWrite}
                      onChange={(e) => setTstate((t) => ({ ...t, team: { ...t.team, [key]: e.target.value } }))}
                      style={inp} placeholder="—" />
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── Medical Summary (editable) ──────────────────────────── */}
          <Section title="Medical Summary (doctor-generated)" icon={<FileCheck2 size={15} />}>
            {!form ? null : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {[
                  ["primary_diagnosis", "Primary Diagnosis"], ["secondary_diagnoses", "Secondary Diagnoses"],
                  ["key_symptoms", "Key Symptoms"], ["functional_limitations", "Functional Limitations"],
                  ["patient_goals", "Patient Goals"],
                ].map(([k, label]) => (
                  <div key={k}>
                    <label style={lbl}>{label}</label>
                    <textarea rows={2} value={form[k]} disabled={!canWrite}
                      onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                      style={{ ...inp, resize: "vertical" }} />
                  </div>
                ))}
                <div>
                  <label style={lbl}>Medical Clearance</label>
                  <select value={form.clearance_status} disabled={!canWrite}
                    onChange={(e) => setForm((f) => ({ ...f, clearance_status: e.target.value }))} style={inp}>
                    <option value="">— not set —</option>
                    {Object.entries(CLEARANCE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <label style={{ ...lbl, marginTop: 6 }}>Clearance Notes</label>
                  <textarea rows={2} value={form.clearance_notes} disabled={!canWrite}
                    onChange={(e) => setForm((f) => ({ ...f, clearance_notes: e.target.value }))}
                    style={{ ...inp, resize: "vertical" }} />
                </div>
              </div>
            )}
            {canWrite && (
              <button onClick={saveSummary} disabled={busy} style={{ ...btnPrimary, marginTop: 10 }}>
                {busy ? <><Loader2 size={12} className="spin" /> Saving…</> : <><Save size={13} /> Save Medical Summary</>}
              </button>
            )}
            {s.clearance_status && (
              <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: CLEARANCE_COLOR[s.clearance_status] || "#5a6478" }}>
                Clearance: {CLEARANCE_LABELS[s.clearance_status] || s.clearance_status}
              </div>
            )}
          </Section>

          {/* ── Clinical AI (Phase D, feature-flagged) ──────────────── */}
          <Section title="Clinical Research & Protocol AI" icon={<Brain size={15} />}>
            {!(capability && capability.enabled) ? (
              <Empty>{(capability && capability.note) || "Clinical AI is disabled pending research-corpus selection and clinical/legal sign-off."}</Empty>
            ) : (
              <>
                <div style={{ fontSize: 11.5, color: "#6C757D", marginBottom: 8 }}>{capability.disclaimer}</div>
                <textarea rows={2} value={conditions} onChange={(e) => setConditions(e.target.value)}
                  placeholder="Enter condition(s) to research, e.g. lumbar disc disease with radiculopathy…"
                  style={{ ...inp, resize: "vertical" }} disabled={!canWrite} />
                {canWrite && (
                  <button onClick={runAI} disabled={aiBusy || conditions.trim().length < 3} style={{ ...btnPrimary, marginTop: 8 }}>
                    {aiBusy ? <><Loader2 size={12} className="spin" /> Researching…</> : <><Brain size={13} /> Get advisory suggestions</>}
                  </button>
                )}
                {ai && ai.result && (
                  <div style={{ marginTop: 12 }}>
                    {ai.result.summary && <div style={{ fontSize: 12.5, color: "#1F2937", marginBottom: 8 }}>{ai.result.summary}</div>}
                    {(ai.result.protocol_options || []).map((o, i) => (
                      <div key={i} style={{ border: "1px solid #e1e6ed", borderRadius: 6, padding: 10, marginBottom: 8 }}>
                        <div style={{ fontWeight: 700, fontSize: 12.5 }}>{o.title} <span style={{ fontWeight: 400, fontSize: 11, color: "#9aa4b2" }}>· confidence: {o.confidence}</span></div>
                        {(o.components || []).length > 0 && (
                          <div style={{ fontSize: 12, color: "#3a4a5e", margin: "4px 0" }}>
                            {o.components.map((c, j) => <span key={j}>{c.name}{c.dose ? ` ${c.dose}` : ""}{c.route ? ` (${c.route})` : ""}{j < o.components.length - 1 ? " · " : ""}</span>)}
                          </div>
                        )}
                        {o.rationale && <div style={{ fontSize: 12, color: "#6C757D" }}>{o.rationale}{(o.citations || []).length ? ` ${o.citations.map((c) => `[${c}]`).join(" ")}` : ""}</div>}
                      </div>
                    ))}
                    {(ai.contraindications || []).length > 0 && (
                      <div style={{ border: "1px solid #f0b4b4", background: "#fdf3f3", borderRadius: 6, padding: 10, marginTop: 6 }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: "#b00020", marginBottom: 4 }}>⚠ Contraindication warnings</div>
                        {ai.contraindications.map((c, i) => (
                          <div key={i} style={{ fontSize: 12, color: "#7a2222" }}>
                            <b>{c.drug}</b> ({c.option}): {c.warnings.map((w) => w.message).join("; ")}
                          </div>
                        ))}
                      </div>
                    )}
                    {(ai.unscreened_medications || []).length > 0 && (
                      <div style={{ border: "1px solid #f0c36d", background: "#fdf6e9", borderRadius: 6, padding: 10, marginTop: 6, fontSize: 12, color: "#8a6d1b" }}>
                        <b>Not auto-screened — verify manually:</b> {ai.unscreened_medications.join(", ")}.
                        <span style={{ color: "#9a8a5a" }}> These drugs aren't in the structured interaction registry; review for contraindications directly (or run the Medication Safety check).</span>
                      </div>
                    )}
                    {(ai.result.uncertainties || []).length > 0 && (
                      <div style={{ fontSize: 11.5, color: "#7c5210", marginTop: 6 }}>Uncertainties: {ai.result.uncertainties.join("; ")}</div>
                    )}
                    <div style={{ fontSize: 11, color: "#9aa4b2", marginTop: 8, fontStyle: "italic" }}>{ai.disclaimer}</div>
                  </div>
                )}
              </>
            )}
          </Section>

          {/* ── Allergies + Medications ─────────────────────────────── */}
          <Section title="Allergies" icon={<AlertTriangle size={15} />}>
            {(data.allergies || []).length === 0 ? <Empty>None recorded.</Empty> : (
              <ul style={ul}>{data.allergies.map((a, i) => (
                <li key={i}><b>{a.allergen}</b>{a.severity ? ` · ${a.severity}` : ""}{a.reaction ? ` — ${a.reaction}` : ""}</li>
              ))}</ul>
            )}
          </Section>

          <Section title="Current Medications" icon={<Pill size={15} />}>
            {(data.medications || []).length === 0 ? <Empty>None recorded.</Empty> : (
              <table style={tbl}><thead><tr><th style={th}>Medication</th><th style={th}>Dose</th><th style={th}>Frequency</th><th style={th}>Reason</th></tr></thead>
                <tbody>{data.medications.map((m, i) => (
                  <tr key={i}><td style={td}>{m.drug_name}</td><td style={td}>{m.dosage || "—"}</td><td style={td}>{m.frequency || "—"}</td><td style={td}>{m.notes || "—"}</td></tr>
                ))}</tbody></table>
            )}
          </Section>

          {/* ── Therapy Plan + Product Verification ─────────────────── */}
          <Section title="Therapy Plan & Product Verification" icon={<Syringe size={15} />}>
            {(data.administrations || []).length === 0 ? <Empty>No administrations recorded yet.</Empty> : (
              <table style={tbl}><thead><tr><th style={th}>Product</th><th style={th}>Route</th><th style={th}>Count</th><th style={th}>Viability</th><th style={th}>Lot</th><th style={th}>QC</th></tr></thead>
                <tbody>{data.administrations.map((r) => (
                  <tr key={r.id}>
                    <td style={td}>{MODALITY_LABEL[r.modality] || r.modality}</td>
                    <td style={td}>{r.route ? r.route.replace(/_/g, " ") : "—"}</td>
                    <td style={td}>{r.administered_count != null ? `${Number(r.administered_count).toLocaleString()} ${r.count_unit || ""}` : "—"}</td>
                    <td style={td}>{r.viability_pct != null ? `${r.viability_pct}%` : "—"}</td>
                    <td style={td}>{r.lot_number || "—"}</td>
                    <td style={td}>{r.lot_number ? (r.qc_certificate_id ? <span style={{ color: "#15803D", fontWeight: 700 }}>on file</span> : <span style={{ color: "#B45309", fontWeight: 700 }}>missing</span>) : "—"}</td>
                  </tr>
                ))}</tbody></table>
            )}
          </Section>

          {/* ── Treatment Checklist (§11) ───────────────────────────── */}
          {tstate && (
            <Section title="Treatment Checklist" icon={<FileCheck2 size={15} />}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
                {Object.entries(CHECKLIST).map(([group, items]) => (
                  <div key={group}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>{group} Procedure</div>
                    {items.map(([key, label]) => (
                      <label key={key} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#1F2937", padding: "3px 0", cursor: canWrite ? "pointer" : "default" }}>
                        <input type="checkbox" checked={!!tstate.checklist[key]} disabled={!canWrite}
                          onChange={(e) => setTstate((t) => ({ ...t, checklist: { ...t.checklist, [key]: e.target.checked } }))} />
                        {label}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              {canWrite && (
                <button onClick={saveTreatmentState} disabled={tbusy} style={{ ...btnPrimary, marginTop: 10 }}>
                  {tbusy ? <><Loader2 size={12} className="spin" /> Saving…</> : <><Save size={13} /> Save Checklist</>}
                </button>
              )}
            </Section>
          )}

          {/* ── Consent + Follow-ups ────────────────────────────────── */}
          <Section title="Consent" icon={<FileCheck2 size={15} />}>
            {(data.consent || []).length === 0 ? <Empty>No signatures on file.</Empty> : (
              <ul style={ul}>{data.consent.map((c, i) => (
                <li key={i}>{c.signer_role === "physician" ? "Physician" : "Patient"} — {c.signer_name} · {fmtDate(c.signed_at)}</li>
              ))}</ul>
            )}
          </Section>

          <Section title="Follow-Up Schedule" icon={<CalendarClock size={15} />}>
            {(data.follow_ups || []).length === 0 ? <Empty>No follow-ups generated yet.</Empty> : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {data.follow_ups.map((f, i) => (
                  <span key={i} style={{ fontSize: 11.5, padding: "3px 9px", borderRadius: 12, border: "1px solid #c9d6e8", background: f.completed_at ? "#eef6ee" : "#fff", color: f.completed_at ? "#2d6b2d" : "#3a4a5e" }}>
                    {f.cadence_marker} · {fmtDate(f.due_at)}{f.completed_at ? " ✓" : ""}
                  </span>
                ))}
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

function Band({ title, icon, children, danger }) {
  return (
    <div style={{ border: `1px solid ${danger ? "#f0b4b4" : "#e1e6ed"}`, background: danger ? "#fdf3f3" : "#fff", borderRadius: 6, padding: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.04em", color: danger ? "#b00020" : "#5a6478", fontWeight: 700, marginBottom: 4 }}>
        {icon} {title}
      </div>
      <div style={{ fontSize: 12.5, color: "#1F2937", overflow: "hidden", textOverflow: "ellipsis" }}>{children}</div>
    </div>
  );
}
function Section({ title, icon, children }) {
  return (
    <div style={{ border: "1px solid #e1e6ed", background: "#fff", borderRadius: 6, padding: 14, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10, color: "#2E8B98" }}>
        {icon}<strong style={{ fontSize: 13.5, color: "#1F2937" }}>{title}</strong>
      </div>
      {children}
    </div>
  );
}
function Grid({ items }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
      {items.map(([k, v], i) => (
        <div key={i}><div style={{ fontSize: 10.5, color: "#9aa4b2", textTransform: "uppercase" }}>{k}</div><div style={{ fontSize: 13, color: "#1F2937" }}>{v || "—"}</div></div>
      ))}
    </div>
  );
}
function Empty({ children }) { return <div style={{ fontSize: 12, color: "#9aa4b2", fontStyle: "italic" }}>{children}</div>; }

const lbl = { display: "block", fontSize: 11, fontWeight: 600, color: "#3a4a5e", margin: "0 0 3px" };
const inp = { width: "100%", padding: "6px 8px", fontSize: 12.5, color: "#1f2a3b", border: "1px solid #c9d6e8", borderRadius: 4, background: "#fff", boxSizing: "border-box", fontFamily: "inherit" };
const tbl = { width: "100%", borderCollapse: "collapse", fontSize: 12.5 };
const th = { textAlign: "left", padding: "4px 8px", color: "#6C757D", borderBottom: "1px solid #e1e6ed", fontSize: 11, textTransform: "uppercase" };
const td = { padding: "5px 8px", borderBottom: "1px solid #f0f3f7", color: "#1F2937" };
const ul = { margin: 0, paddingLeft: 18, fontSize: 12.5, color: "#1F2937" };
const btnPrimary = { padding: "7px 14px", fontSize: 12.5, fontWeight: 600, background: "#2E8B98", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 };
const errBox = { display: "flex", alignItems: "center", gap: 6, background: "#fde8e8", border: "1px solid #f5a5a5", padding: 10, borderRadius: 4, marginBottom: 12, color: "#8b0000", fontSize: 13 };
